"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import CustomInput from "./CustomInput";
import { authFormSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { signIn } from "@/lib/actions/user.actions";

const AuthForm = ({ domain_configuration }: { domain_configuration: string|undefined }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const formSchema = authFormSchema();

  const is_labelleconcierge = domain_configuration === "labelleconciergerie";

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setError("");
    setIsLoading(true);
    try {
      const parsed_username =
        "33" +
        data.username.replaceAll(" ", "").replaceAll(".", "").replace(/^0/, "");
      const parsed_password = data.password.replaceAll(" ", "");

      const userData = {
        username: parsed_username,
        password: parsed_password,
        url: "/api/login/",
      };
      console.log('onSubmit1');
      const response = await signIn(userData);
      setIsLoading(false);
      if (response && response.error) {
        setError(response.error);
      }
    } catch (err) {
      setIsLoading(false);
      console.error('AuthForm/onSubmit', err);
    }
  };

  return (
    <section className="auth-form">
      <header className="flex flex-col gap-5 md:gap-8">
        <Link href={`${is_labelleconcierge ? "https://www.labelleconciergerie.com/": "https://www.taxisconnect.com/"}`} className="flex cursor-pointer items-center gap-1">
          <Image
            src={`${is_labelleconcierge ? "/icons/labelleconciergerie_icon.png": "/icons/icon@2x.png"}`}
            alt="Horizon Logo"
            width={34}
            height={40}
            className="rounded-md"
          />
          <h1 className="text-26 font-ibm-plex-serif font-bold text-black-1 ml-2">
            
            {is_labelleconcierge ? "La belle conciergerie": "TaxisConnect"}
          </h1>
        </Link>
      </header>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <CustomInput
              control={form.control}
              name={"username"}
              label={"Téléphone"}
              placeholder={"Indiquez votre numéro de téléphone"}
            />
            <CustomInput
              control={form.control}
              name={"password"}
              label={"Mot de passe"}
              placeholder={"Indiquez votre mot de passe"}
            />
            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="flex flex-col gap-4">
              <Button type="submit" className="form-btn" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex">
                    <Loader2 size={20} className="animate-spin" /> &nbsp;
                    Loading...
                  </div>
                ) : (
                  "Se connecter"
                )}
              </Button>
            </div>
          </form>
        </Form>
        <footer className="flex justify-center gap-2 flex-col">
          <div className="text-12 font-normal text-gray-600 mt-2">
            Pour nous joindre :{" "}
            <a href={`mailto:${is_labelleconcierge ? "contact@labelleconciergerie.com": "commercial@taxisconnect.com"}`} className="underline">
              {is_labelleconcierge ? "contact@labelleconciergerie.com": "commercial@taxisconnect.com"}
            </a>{" "}
            ou {is_labelleconcierge ? "09 75 128 128": "09 75 120 120"} (appel local) aux heures de bureau
          </div>
          <div className="flex flex-col justify-between">
          <Link href={'/forgotten-password'}>
            <p className="text-14 font-semibold text-blue-600">
              Mot de passe oublié ?
            </p>
          </Link>
          {is_labelleconcierge ? null : (
          <Link href={'https://www.taxisconnect.com/abonne/'}>
            <p className="text-14 font-semibold text-blue-600">
              Allez à l&lsquo;ancien site
            </p>
          </Link>)}
          {is_labelleconcierge ? null : (
          <Link href={'/conventionne'}>
            <p className="text-14 font-semibold text-blue-600">
              Commander un taxi conventionné
            </p>
          </Link>)}
          {is_labelleconcierge ? null : (
          <Link href={'/driver-sign-in'}>
            <p className="text-14 font-semibold text-blue-600">
              Se connecter comme chauffeur
            </p>
          </Link>)}
          </div>
        </footer>
      </div>
    </section>
  );
};

export default AuthForm;
